/**
 * Points for the client about the month as a whole (D-130, W3-19 (8)).
 *
 * Review raises one row with the client (D-79). Some questions belong to no row — a Table 12
 * split, a month's credit notes, whether a register is complete — so they are raised here, on
 * the month. Each names what it is about in the return's own words and carries the question the
 * client reads on the workbook's *To confirm* sheet. A point changes no figure; raising or
 * withdrawing one on a sent month sends its GSTR-1 back to Prepared, as a raised row does.
 */
import { Button, Card } from '@fiveox/ui'
import { useCallback, useEffect, useState } from 'react'

import { type MonthPointsBody, periods } from '../lib/api'

export function MonthPointsCard({
  periodId,
  canRaise,
  busy,
  act,
}: {
  periodId: string
  canRaise: boolean
  busy: boolean
  /** The screen's own action runner: shows its error and reloads the month after. */
  act: (fn: () => Promise<unknown>) => Promise<void>
}) {
  const [body, setBody] = useState<MonthPointsBody | null>(null)
  const [raising, setRaising] = useState<{ subject: string; note: string } | null>(null)

  const load = useCallback(async () => {
    try {
      setBody((await periods.monthPoints(periodId)).data)
    } catch {
      setBody(null)
    }
  }, [periodId])

  useEffect(() => {
    void load()
  }, [load])

  if (!body || (body.points.length === 0 && !canRaise)) return null

  return (
    <Card className="mt-6 p-4">
      <p className="gst-panel-h mb-1">Points for the client about this month</p>
      <p className="gst-meta mb-3">
        A question about the month as a whole, not one row — listed on the client
        workbook&rsquo;s <em>To confirm</em> sheet. A point changes no figure.
      </p>
      {body.points.length === 0 && <p className="gst-meta">None raised.</p>}
      {body.points.map((point) => (
        <p key={point.id} className="gst-meta mb-1">
          <strong>{point.what}:</strong> {point.note}{' '}
          {canRaise && (
            <button
              type="button"
              className="gst-link"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  await periods.withdrawMonthPoint(periodId, point.id)
                  await load()
                })
              }
            >
              Withdraw
            </button>
          )}
        </p>
      ))}
      {canRaise && !raising && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="mt-2"
          disabled={busy}
          onClick={() => setRaising({ subject: 'month', note: '' })}
        >
          Raise a point about this month
        </Button>
      )}
      {canRaise && raising && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            value={raising.subject}
            onChange={(e) => setRaising({ ...raising, subject: e.target.value })}
            className="gst-input"
            aria-label="What the point is about"
          >
            {Object.entries(body.subjects).map(([key, words]) => (
              <option key={key} value={key}>
                {words}
              </option>
            ))}
          </select>
          <input
            value={raising.note}
            onChange={(e) => setRaising({ ...raising, note: e.target.value.slice(0, 500) })}
            placeholder="What should the client confirm? They read this on their workbook"
            aria-label="What the client should confirm"
            className="gst-input min-w-56 flex-1"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy || !raising.note.trim()}
            onClick={() =>
              void act(async () => {
                await periods.raiseMonthPoint(periodId, raising)
                setRaising(null)
                await load()
              })
            }
          >
            Raise it with the client
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setRaising(null)}>
            Cancel
          </Button>
        </div>
      )}
    </Card>
  )
}
